import clsx from 'clsx';

import { Icon } from '@gitbook/spine-icons';

import { type LinkHookProps, useLink } from '../Button';
import { Tooltip } from '../Tooltip';
import type { BadgeProps } from './Badge';
import { BadgeContent } from './BadgeContent';
import { useBadge } from './useBadge';

export interface BadgeLinkProps extends LinkHookProps, BadgeProps {
    /** The tooltip content */
    tooltip?: string;
}

export function BadgeLink({
    ref,
    ...props
}: BadgeLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) {
    const { tooltip, kind, weight } = props;

    const { badgeProps, isEmpty, isIconOnly, isSingleChar, iconSize, isNumber } = useBadge(props);
    const { linkProps } = useLink(props);

    // Show the arrow only if there is a label and not an icon only badge
    const isArrowVisible = !isIconOnly && !isSingleChar && !isEmpty;

    // If the badge is disabled or there is no href or onClick, we should not render the link
    const isDisabled = Boolean(linkProps.disabled) || (!linkProps.href && !linkProps.onClick);

    return (
        <Tooltip label={tooltip}>
            <a
                {...linkProps}
                {...badgeProps}
                ref={ref}
                className={clsx(
                    'inline-flex no-underline transition-all',
                    {
                        'hover:brightness-[0.95] dark:hover:brightness-[1.1]': !isDisabled,
                    },
                    badgeProps.className
                )}
            >
                <BadgeContent
                    {...props}
                    iconSize={iconSize}
                    isIconOnly={isIconOnly}
                    isNumber={isNumber}
                    kind={kind ?? 'primary'}
                    weight={weight}
                />
                {isArrowVisible && <Icon.ArrowUpRight size={iconSize} />}
            </a>
        </Tooltip>
    );
}
